import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, breadcrumbBadge }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-[18px] sm:text-[20px] font-semibold tracking-tight text-brand-brown truncate" data-testid="page-title">
            {title}
          </h1>
          {breadcrumbBadge}
        </div>
        {subtitle && (
          <p className="text-[12px] sm:text-[13px] text-slate-500 mt-0.5 line-clamp-2 sm:line-clamp-none">
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}

export function PageContainer({ children, className }) {
  return (
    <div className={cn("p-3 sm:p-4 md:p-6 max-w-[1600px] mx-auto w-full min-w-0", className)} data-testid="page-container">
      {children}
    </div>
  );
}

export function Card({ children, className, padded = true }) {
  return (
    <div
      className={cn(
        "bg-white border border-brand-cream-border rounded-xl shadow-sm min-w-0",
        padded && "p-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action, className }) {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-2 mb-3 min-w-0", className)}>
      <h2 className="text-[14px] font-semibold tracking-tight text-brand-brown">{children}</h2>
      {action}
    </div>
  );
}

export function Pill({ tone = "default", children, className }) {
  const tones = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-brand-cream-panel text-brand-orange border-orange-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    purple: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 h-5 rounded-md text-[11px] font-medium border whitespace-nowrap",
        tones[tone] || tones.default,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusDot({ tone = "default", className }) {
  const colors = {
    default: "bg-slate-400",
    green: "bg-emerald-500",
    red: "bg-red-500",
    amber: "bg-amber-500",
    blue: "bg-blue-500",
  };
  return (
    <span className={cn("inline-block h-1.5 w-1.5 rounded-full", colors[tone], className)} />
  );
}

export function EmptyHint({ children }) {
  return (
    <div className="text-center text-[12px] text-slate-400 py-8 border border-dashed border-slate-200 rounded-lg">
      {children}
    </div>
  );
}
